// MACHINERY V2 (Stroje) — Fleet V4 body: the shop-floor wall / kiosk (M2.14).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.14 + desktop/README.md §V4.
// Rendered truth: desktop/screenshots/v4-wall--refined-claude.png and
// v4-wall--machineid-native.png (both viewed before any of this wave's code was
// written, per the view-before-code rule). Reference impl:
// desktop/machinery-v4-wall.jsx (V4Screen).
//
// ============================================================
//  WHY THIS ONE BODY REPLACES THE WHOLE PAGE, RAIL AND ALL
// ============================================================
// V1, V2 and V3 are bodies INSIDE the fleet shell (rail + toolbar +
// FleetContent). V4 is not: picking the wall switches the page into KIOSK
// layout — no filter rail, no toolbar, no group headers, no chrome beyond this
// view's own header (README §V4: "no chrome tabs — custom header"; ticket
// M2.14: "NO rail, NO interactions, NO chrome tabs" — the INTERACTIONS half of
// that quote was amended by owner ruling 2026-08-24, see below). So FleetPage
// branches to this component BEFORE composing its chrome, and everything the
// wall needs arrives as props.
//
// >>> IT STILL RUNS ON THE ONE POLLER. <<< FleetPage calls useFleetPageData
// (and therefore useFleetTelemetry) ABOVE its branch, unconditionally — hooks
// must be — so the wall consumes exactly the same single 60 s batch read as
// every other view. M2.14 adds NO second poller, NO second fetch and NO second
// endpoint. The one timer it does add is the display clock (useWallClock), and
// it touches no network. That clock lives in a LEAF (WallLiveClock), NOT here:
// held at this root, its 1 Hz tick rebuilt every tile in the grid once a second
// for as long as the wall was up. Do not hoist it back.
//
// ChromeNavV4 STILL RENDERS ABOVE THIS (MachineryV2Layout mounts it outside the
// Outlet). Full kiosk chrome purity — hiding the platform nav too — is an M2.12
// cutover concern, not this sub-phase's.
//
// ============================================================
//  IT WAS A ONE-WAY DOOR. IT NOW HAS THREE WAYS BACK.
// ============================================================
// The wall renders no toolbar, therefore no view toggle; and `fleetView` has
// been localStorage-persisted since M2.13 (ruling 3). So a user who picked the
// wall STAYED on it across reloads, with no in-page control to leave — a trap
// for anyone who clicked the glyph out of curiosity. Three ways back:
//   1. THE X in the header's top-right — persistent and always visible, "just
//      like we have in the Settings menu" (owner ruling 2026-08-24).
//      WallExitButton.tsx.
//   2. Esc — the keyboard trigger for the SAME callback, bound below.
//   3. `?view=V1` on the URL — FleetPage; a recoverable path for a stuck user
//      or for support.
//
// >>> THE OWNER RULING SUPERSEDED THE KIOSK-PURITY HALF OF THE EARLIER ONE. <<<
// M2.14 shipped with no visible control at all (README §V4: "opened fullscreen
// on the hall TV and left there", "no hover states — it is a TV"; ticket
// M2.14: "NO interactions") and taught Esc through a chip that faded in on
// pointer movement. The owner tested that on .cc and asked for the X instead.
// The chip was then DELETED rather than kept alongside it: its whole purpose
// was discoverability, and a permanent glyph does that strictly better —
// including for the first-time user who never moves a mouse to summon
// anything. Two exit affordances on a surface meant to be read from across a
// hall is one too many.
//
// So the rule now reads: NO INTERACTIONS ON THE DATA SURFACE. Every tile stays
// completely inert (WallTile.tsx's header); the X is header chrome, sanctioned
// by the ruling, and the only focusable node this view renders.
//
// AND IT SHOWS THE WHOLE ACTIVE PARK. The scope chips are hidden here too, so
// FleetPage derives the wall's scope as `active` (never mutating the user's
// selection) and hands this view the UN-FACETED base set. See its kiosk-branch
// note: a monitoring wall that silently inherits somebody's alarm filter is the
// exact lie this surface must not tell.
//
// ============================================================
//  M2.16b — BELOW 1280 THE WALL RECOMPOSES, IT DOES NOT SHRINK
// ============================================================
// Boards 12 and 13 replace the 380px tile grid with a compact STATUS LIST: one
// column on the phone, two at 1024, a 62px row instead of a 280px tile. That is
// WallMobileView, and this component branches to it below 1280 — after every
// hook, so the single poller, the Esc binding and the counts are shared and the
// hook count is identical at every width.
//
// >>> WHAT STAYS HERE IS THE 1920 TV WALL, UNCHANGED. <<< The tile grid, its
// auto-fit column arithmetic, WallHeader and WallTile are not touched by this
// sub-phase — the desktop wall was shipped and owner-tested, and the phone list
// is a different composition, not a responsive version of the same one. In
// particular the phone's alarms-first ORDERING lives in WallMobileView and must
// never be lifted up here: it would reach the hall TV through this component's
// own `machines` prop. See that file's header for why a wall that reorders
// itself is the worse failure.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useEffect, useMemo } from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { V2_BELOW_DESKTOP, useV2MediaQuery } from "../../useV2MediaQuery";
import { Machine } from "../../../models/models";
import { Notice } from "../FleetNotices";
import {
  MachineryV2CardState,
  MachineryV2Telemetry,
  deriveCardPresentation,
  unconnectedTelemetry,
} from "../cardStates";
import { buildLocationTree, countStates } from "../fleetSelectors";
import WallHeader from "./WallHeader";
import WallMobileView from "./WallMobileView";
import WallTile from "./WallTile";

/**
 * FOUR COLUMNS AT THE 1920 DESIGN WIDTH, FEWER BELOW — in one CSS expression.
 *
 * The artboard is drawn at 1920x1080 with a hard `repeat(4, 1fr)`. Copied
 * literally, a 1280 desktop would give 300px tiles holding a 28px machine name,
 * and the brief's "must degrade gracefully at normal desktop widths" would
 * fail. A JS breakpoint was the alternative, but the module's boundaries top
 * out at a single >=1280 desktop tier and cannot tell 1280 from 1920 — the
 * exact distinction that matters here.
 *
 * So the track list does the arithmetic itself. With 20px padding either side
 * and a 14px gap, `auto-fit` fits N columns while N*380 + (N-1)*14 <= width-40:
 *   1920 -> 4  (5 would need 364px tracks, below the 380 floor)
 *   1600 -> 3      1280 -> 3      1024 -> 2      <760 -> 1
 * i.e. EXACTLY the artboard's four at the design size, degrading 4 -> 3 -> 2 ->
 * 1 with no hard break anywhere, least of all at the 1280 baseline. The
 * `min(100%, 380px)` floor is what keeps the track from overflowing its
 * container on a narrow window. Phone/tablet recomposition is M2.16, not this.
 */
const WALL_GRID_COLUMNS = "repeat(auto-fit, minmax(min(100%, 380px), 1fr))";

/** How many plant names the header prints before it summarises the rest. */
const PLANT_NAME_LIMIT = 3;

export interface FleetWallViewProps {
  /** The machines the wall renders — the page's `flat`, i.e. the UN-FACETED
   *  active set, flattened and de-duplicated. Deliberately NOT `visibleFlat`:
   *  the kiosk hides every control that narrows it, so an inherited filter
   *  would be invisible (FleetPage's kiosk branch). The wall is ONE flat grid
   *  with no group headers, exactly as the artboard draws it, so it also does
   *  not take `sections` (where a machine in two lines appears twice). */
  machines: Machine[];
  states: Map<string, MachineryV2CardState>;
  telemetry: Map<string, MachineryV2Telemetry>;
  /** Poll health for the header's liveness dot — see WallLiveClock. */
  lastTelemetryOkAt: number;
  isLoading: boolean;
  /** Leaves the wall for V1. FleetPage's `exitWall`, run by BOTH triggers: the
   *  header's X (threaded into WallHeader) and the Esc binding below. */
  onExit: () => void;
}

const FleetWallView: React.FC<FleetWallViewProps> = ({
  machines,
  states,
  telemetry,
  lastTelemetryOkAt,
  isLoading,
  onExit,
}) => {
  const { translate } = useTranslation("myMachinery");
  // F-A: below 1280 is read as the media QUERY both v2 stylesheets fence on,
  // never as an integer width. Identical at every integer width; correct in
  // the fractional band too. See useV2MediaQuery.ts.
  const isDesktop = !useV2MediaQuery(V2_BELOW_DESKTOP);

  // Counted over the machines THIS VIEW RENDERS — which, since the fix pass, is
  // the whole active park. See WallHeader's header for why that identity
  // matters on a surface with no filter rail to explain a mismatch.
  const counts = useMemo(() => countStates(machines, states), [machines, states]);

  // The artboard's "Zavod Brno 1 + Trencin" line, derived from real data: the
  // top level of the shipped location tree over the same machines. Reused, not
  // re-derived — buildLocationTree already keys parents on
  // `parentLocationName || locationName` (fleetSelectors.ts:107-114, personally
  // re-read at commit time).
  //
  // THE OVERFLOW IS A TRANSLATED PHRASE, NOT A BARE NUMERAL. It used to be
  // appended with the same " + " separator the names use, so four plants read
  // "Brno + Trencin + Praha + 3" — a site called "3". It is now the only piece
  // of this line the header styles differently, and the only string in the wave
  // that had no translation key.
  const plant = useMemo(() => {
    const names = buildLocationTree(machines).map((n) => n.name);
    if (names.length === 0) {
      return {
        label: translate("myMachinery:v2.fleet.machineCount", { count: machines.length }),
        more: null,
      };
    }
    if (names.length <= PLANT_NAME_LIMIT) return { label: names.join(" + "), more: null };
    return {
      label: names.slice(0, PLANT_NAME_LIMIT).join(" + "),
      more: translate("myMachinery:v2.fleet.wall.morePlants", {
        count: names.length - PLANT_NAME_LIMIT,
      }),
    };
  }, [machines, translate]);

  /**
   * ESC — the keyboard trigger for the wall's one exit.
   *
   * It sits at this ROOT, unlike the clock, because it holds NO STATE: a
   * keydown listener re-renders nothing, so hosting it here costs the tile grid
   * exactly zero. (WallLiveClock is a leaf for the opposite reason — its 1 Hz
   * tick would rebuild every tile.) The binding MOVED here from the deleted
   * hint chip, which owned it only because it happened to be the file that also
   * drew the chip; deleting the chip must not delete Esc.
   *
   * `onExit` is FleetPage's `exitWall` — literally the same callback the header
   * X is handed one JSX block below, so the two triggers cannot drift apart.
   * The listener is on `window` and is removed on unmount, so Esc does nothing
   * anywhere else in the fleet.
   */
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onExit();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onExit]);

  // BELOW EVERY HOOK, so the phone list runs on the same single poller and the
  // same Esc binding, and no hook count changes on rotation. See this file's
  // M2.16b header block.
  if (!isDesktop) {
    return (
      <WallMobileView
        machines={machines}
        states={states}
        telemetry={telemetry}
        lastTelemetryOkAt={lastTelemetryOkAt}
        isLoading={isLoading}
        plant={plant.label}
        plantMore={plant.more}
        counts={counts}
        onExit={onExit}
      />
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        overflow: "hidden",
        background: "var(--bg)",
      }}
    >
      <WallHeader
        plant={plant.label}
        plantMore={plant.more}
        counts={counts}
        lastTelemetryOkAt={lastTelemetryOkAt}
        onExit={onExit}
      />

      {/* The prototype clips its grid (`overflow: hidden`) because it holds
          exactly nine machines and always fits. A real fleet does not: clipping
          would SILENTLY DROP machines off a monitoring wall, which is the one
          failure this surface must never have. So the grid scrolls instead.
          A TV will not scroll it, but nothing is hidden without a scrollbar
          saying so, and at a desk the whole fleet is reachable. */}
      <main style={{ flex: 1, minHeight: 0, padding: 20, overflowY: "auto" }}>
        {isLoading ? (
          <Notice text={translate("myMachinery:v2.fleet.loading")} dashed={false} />
        ) : machines.length === 0 ? (
          <Notice text={translate("myMachinery:noMachinesFound")} />
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: WALL_GRID_COLUMNS,
              gridAutoRows: "minmax(280px, auto)",
              gap: 14,
            }}
          >
            {machines.map((m) => {
              const t = telemetry.get(m.id) || unconnectedTelemetry();
              return (
                <WallTile
                  key={m.id}
                  machine={m}
                  presentation={deriveCardPresentation(t)}
                  telemetry={t}
                />
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default FleetWallView;
